import React, { useState, useEffect } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const PWAUpdateNotification: React.FC = () => {
  const [showUpdateNotification, setShowUpdateNotification] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      // Verificar se há uma atualização disponível
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        // Service worker foi atualizado, recarregar a página
        window.location.reload();
      });

      // Detectar quando uma nova versão está disponível
      const handleUpdateAvailable = (registration: ServiceWorkerRegistration) => {
        const newWorker = registration.waiting;
        if (newWorker) {
          setWaitingWorker(newWorker);
          setShowUpdateNotification(true);
        }
      };

      // Verificar atualizações em service workers já registrados
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(registration => {
          if (registration.waiting) {
            handleUpdateAvailable(registration);
          }
          
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  handleUpdateAvailable(registration);
                }
              });
            }
          });
        });
      });
    }
  }, []);

  const handleUpdate = () => {
    if (waitingWorker) {
      // Enviar mensagem para o service worker ativar a nova versão
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
      setShowUpdateNotification(false);
      
      toast({
        title: "Atualizando aplicação...",
        description: "A página será recarregada em instantes.",
      });
    }
  };

  const handleDismiss = () => {
    setShowUpdateNotification(false);
    // Mostrar novamente após 5 minutos
    setTimeout(() => {
      if (waitingWorker) {
        setShowUpdateNotification(true);
      }
    }, 5 * 60 * 1000);
  };

  if (!showUpdateNotification) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-50 max-w-sm">
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0">
          <RefreshCw className="h-6 w-6 text-blue-600" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-medium text-gray-900">
            Nova versão disponível
          </h3>
          <p className="text-xs text-gray-600 mt-1">
            Uma nova versão do Fox Vendas está disponível. Atualize para obter as últimas melhorias.
          </p>
        </div>
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 text-gray-400 hover:text-gray-600"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="mt-3 flex space-x-2">
        <button
          onClick={handleUpdate}
          className="flex-1 bg-blue-600 text-white text-xs py-2 px-3 rounded-md hover:bg-blue-700 transition-colors"
        >
          Atualizar Agora
        </button>
        <button
          onClick={handleDismiss}
          className="text-gray-500 text-xs py-2 px-3 hover:text-gray-700 transition-colors"
        >
          Mais tarde
        </button>
      </div>
    </div>
  );
};

export default PWAUpdateNotification;